import { useEffect, useState } from "react";
import styles from "../../styles/chatStyles";

import { db } from "../../firebase";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { useModal } from "../../ModalContext";
import AttachmentsModal from "./AttachmentsModal";

import GroupAvatar from "../ui/GroupAvatar";
import { getCachedUser, setCachedUser } from "../../utils/userCache";

export default function ChatHeader({ chat }) {
  const [typing, setTyping] = useState(null);
  const [lastSeen, setLastSeen] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [members, setMembers] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);

  const { setGroupModal } = useModal();
  const currentUser = localStorage.getItem("user");

  const isGroup = chat?.isGroup;

  // 💥 ВАЖНО: безопасный chatId
  const chatId = chat
    ? isGroup
      ? chat.id
      : chat?.user?.username
      ? [currentUser, chat.user.username].sort().join("_")
      : null
    : null;

  // ===============================
  // 🧠 имя группы
  // ===============================
  const getGroupName = () => {
    if (groupData?.name) return groupData.name;

    if (!members.length) return "Группа";

    const names = members
      .filter((u) => u.username !== currentUser)
      .map((u) => u.name || u.username);

    return names.length ? names.slice(0, 3).join(", ") : "Группа";
  };

  // ===============================
  // 🔥 CHAT SNAPSHOT
  // ===============================
  useEffect(() => {
    if (!chatId) return;

    const unsub = onSnapshot(doc(db, "chats", chatId), (snap) => {
      if (!snap.exists()) return;

      const data = snap.data();
      setTyping(data.typing || null);

      if (isGroup) {
        setGroupData(data);
      }
    });

    return () => unsub();
  }, [chatId, isGroup]);

  // ===============================
  // 🟢 ONLINE (PRIVATE)
  // ===============================
  useEffect(() => {
  if (isGroup || !chat?.user?.username) return;

  const unsub = onSnapshot(
    doc(db, "users", chat.user.username),
    (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setLastSeen(data.lastSeen || 0);
        setViewing(data.viewing || null);
      }
    }
  );

  return () => unsub();
}, [chat?.user?.username, isGroup]);

  // ===============================
  // 👥 LOAD MEMBERS
  // ===============================
  useEffect(() => {
    if (!isGroup || !groupData?.members) return;

    let mounted = true;

    const load = async () => {
      const result = [];

      for (let username of groupData.members) {
        let cached = getCachedUser(username);

        if (!cached) {
          const snap = await getDoc(doc(db, "users", username));
          if (snap.exists()) {
            cached = snap.data();
            setCachedUser(username, cached);
          }
        }

        if (cached) {
          result.push({
            username,
            ...cached,
          });
        }
      }

      if (mounted) setMembers(result);
    };

    load();

    return () => (mounted = false);
  }, [groupData, isGroup]);

  // ===============================
  // 🔁 REALTIME MEMBERS UPDATE
  // ===============================
  useEffect(() => {
    if (!members.length) return;

    const unsubs = members.map((u) =>
      onSnapshot(doc(db, "users", u.username), (snap) => {
        if (!snap.exists()) return;

        const data = snap.data();
        setCachedUser(u.username, data);

        setMembers((prev) =>
          prev.map((x) =>
            x.username === u.username
              ? { username: u.username, ...data }
              : x
          )
        );
      })
    );

    return () => unsubs.forEach((u) => u());
  }, [members.length]);

  // ===============================
  // 🟢 ONLINE TEXT (GROUP)
  // ===============================
  const getOnlineText = () => {
  if (!members.length) return "Группа";

  const now = Date.now();

  const onlineUsers = members.filter(
    (u) => now - (u.lastSeen || 0) < 15000
  );

    if (!onlineUsers.length) return "никто не в сети";

    if (onlineUsers.length <= 2) {
      return onlineUsers.map((u) => u.name || u.username).join(", ");
    }

        if (!onlineUsers.length) return "никто не в сети";

        if (onlineUsers.length === 1) return "1 в сети";

        return `${onlineUsers.length} в сети`;
  };

  // ===============================
  // 💥 SAFE RENDER
  // ===============================
  if (!chat) {
    return (
      <div style={{ ...styles.chatHeader, opacity: 0.5 }}>
        Выбери чат
      </div>
    );
  }

 return (
  <div
    style={{
      ...styles.chatHeader,
      display: "flex",
      alignItems: "center",
    }}
    onClick={() => isGroup && setGroupModal(chat)}
  >
    {/* АВАТАР */}
    {isGroup ? (
      <GroupAvatar
        src={groupData?.avatar || chat?.avatar}
        name={getGroupName()}
        size={42}
      />
    ) : (
      <img
        src={chat?.user?.avatar || "/group-avatar.svg"}
        alt=""
        style={styles.avatar}
      />
    )}

    {/* ТЕКСТ */}
    <div
      style={{
        marginLeft: 10,
        flex: 1,
        minWidth: 0,
      }}
    >
      <div style={{ fontWeight: 600 }}>
        {isGroup
          ? getGroupName()
          : chat?.user?.name || chat?.user?.username}
      </div>

      <div style={styles.status}>
        {isGroup ? (
          typing && typing !== currentUser
            ? "печатает..."
            : getOnlineText()
        ) : typing && typing !== currentUser ? (
          "печатает..."
        ) : viewing === chatId ? (
          "в чате"
        ) : Date.now() - lastSeen < 15000 ? (
          "в сети"
        ) : (
          "не в сети"
        )}
      </div>
    </div>

    {/* ✅ КНОПКА ВНЕ ТЕКСТА */}
    <div
      onClick={(e) => {
        e.stopPropagation();
        setShowAttachments(true);
      }}
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",

        cursor: "pointer",
        transition: "0.2s",
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.6"
      >
        <path d="M21 15V7a5 5 0 0 0-10 0v10a3 3 0 0 0 6 0V8" />
      </svg>
    </div>

    {/* МОДАЛКА */}
    {showAttachments && (
      <AttachmentsModal
        chatId={chatId}
        onClose={() => setShowAttachments(false)}
        onJump={(id) => {
          const el = document.getElementById(id);
          if (!el) return;

          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          window.highlightMessage?.(id);
        }}
      />
    )}
  </div>
);

}